// Shared drag-reorder / tag-toggle building blocks for the sales playbook
// editing UI. Originally private to AddSalesPlaybookModal.tsx; extracted so
// PlaybookIngestionReview.tsx (the AI-draft review screen) can reuse the
// exact same interaction pattern instead of re-implementing drag-reorder
// and tag toggling from scratch.
import React from 'react';
import {
  Delete as TrashIcon,
  DragIndicator as DragIndicatorIcon
} from '@mui/icons-material';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { SalesPlaybookTag } from '../services/apiService';

export const SALES_PLAYBOOK_TAGS: SalesPlaybookTag[] = [
  'situation',
  'pain',
  'cost_math',
  'authority',
  'timeline',
  'close_trigger'
];

export const TAG_LABELS: Record<SalesPlaybookTag, string> = {
  situation: 'Situation',
  pain: 'Pain',
  cost_math: 'Cost Math',
  authority: 'Authority',
  timeline: 'Timeline',
  close_trigger: 'Close Trigger'
};

export interface SortableBulletItemProps {
  id: number;
  value: string;
  placeholder: string;
  canRemove: boolean;
  onChange: (value: string) => void;
  onRemove: () => void;
  inputId?: string;
  annotation?: { label: string; help: string; tone: 'warning' | 'danger' };
}

export const SortableBulletItem: React.FC<SortableBulletItemProps> = ({ id, value, placeholder, canRemove, onChange, onRemove, inputId, annotation }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 }}
      className="flex gap-2 items-center"
    >
      <button
        type="button"
        {...attributes}
        {...listeners}
        className="cursor-grab text-ink-disabled hover:text-ink-muted flex-shrink-0 p-0.5 touch-none"
      >
        <DragIndicatorIcon style={{ fontSize: '1rem' }} />
      </button>
      <div className="flex-1">
        <input
          id={inputId}
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className={`w-full px-3 py-1.5 border rounded-md focus:outline-none focus:ring-2 focus:ring-signature text-sm ${annotation ? (annotation.tone === 'danger' ? 'border-error bg-error-tint' : 'border-warning bg-warning-tint') : 'border-ink-border'}`}
        />
        {annotation && (
          <p className={`mt-1 text-xs ${annotation.tone === 'danger' ? 'text-error' : 'text-warning'}`}>
            <span className="font-semibold">{annotation.label}.</span> {annotation.help}
          </p>
        )}
      </div>
      {canRemove && (
        <button
          type="button"
          onClick={onRemove}
          className="p-1.5 text-error hover:bg-error-tint rounded flex-shrink-0"
        >
          <TrashIcon style={{ fontSize: '1rem' }} />
        </button>
      )}
    </div>
  );
};

export interface SortableQuestionItemProps {
  id: number;
  question: { text: string; tags: SalesPlaybookTag[] };
  index: number;
  canRemove: boolean;
  onTextChange: (value: string) => void;
  onRemove: () => void;
  onTagToggle: (tag: SalesPlaybookTag) => void;
  tagLabels: Record<SalesPlaybookTag, string>;
  allTags: SalesPlaybookTag[];
  inputId?: string;
  annotation?: { label: string; help: string; tone: 'warning' | 'danger' };
}

export const SortableQuestionItem: React.FC<SortableQuestionItemProps> = ({
  id, question, index, canRemove, onTextChange, onRemove, onTagToggle, tagLabels, allTags, inputId, annotation
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 }}
      className="p-2 border border-hairline rounded-lg bg-surface-subtle"
    >
      <div className="flex gap-2 mb-2 items-start">
        <button
          type="button"
          {...attributes}
          {...listeners}
          className="cursor-grab text-ink-disabled hover:text-ink-muted flex-shrink-0 p-0.5 mt-1 touch-none"
        >
          <DragIndicatorIcon style={{ fontSize: '1rem' }} />
        </button>
        <input
          id={inputId}
          type="text"
          value={question.text}
          onChange={(e) => onTextChange(e.target.value)}
          placeholder={`Question ${index + 1}...`}
          className={`flex-1 px-3 py-1.5 border rounded-md focus:outline-none focus:ring-2 focus:ring-signature text-sm ${annotation ? (annotation.tone === 'danger' ? 'border-error bg-error-tint' : 'border-warning bg-warning-tint') : 'border-ink-border bg-surface'}`}
        />
        {canRemove && (
          <button
            type="button"
            onClick={onRemove}
            className="p-1.5 text-error hover:bg-error-tint rounded flex-shrink-0"
          >
            <TrashIcon style={{ fontSize: '1rem' }} />
          </button>
        )}
      </div>
      {annotation && (
        <p className={`mb-2 pl-6 text-xs ${annotation.tone === 'danger' ? 'text-error' : 'text-warning'}`}>
          <span className="font-semibold">{annotation.label}.</span> {annotation.help}
        </p>
      )}
      <div className="flex flex-wrap gap-1 pl-6">
        <span className="text-xs text-ink-muted mr-1">Tags:</span>
        {allTags.map(tag => (
          <button
            key={tag}
            type="button"
            onClick={() => onTagToggle(tag)}
            className={`px-1.5 py-0.5 text-xs rounded-full border transition-colors ${
              question.tags.includes(tag)
                ? 'bg-signature-tint text-signature border-signature'
                : 'bg-surface-subtle text-ink-muted border-hairline hover:bg-surface-sunken'
            }`}
          >
            {tagLabels[tag]}
          </button>
        ))}
      </div>
    </div>
  );
};
